import React from 'react';

import BugReportIcon from '@mui/icons-material/BugReport';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideFeedback: React.FC = () => {
  return (
    <GuidePageLayout
      title="不具合報告・フィードバックを送る"
      description="ツールバーの設定メニューから開発者に不具合報告やフィードバックを送信できます。"
      breadcrumbs={[{ label: '共通機能', path: '/guide/common/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[['ログイン状態', 'ログイン済みであること（権限不問）']]}
        />
      </GuideSection>

      <GuideSection title="設定メニューを開く">
        <GuideStepList
          steps={[
            {
              label: 'ヘッダー右側の歯車アイコン（設定）をクリックする',
              content: (
                <GuideMockContainer label="設定アイコン">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ pointerEvents: 'none' }}
                  >
                    <IconButton size="small">
                      <SettingsIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                    </IconButton>
                    <Typography variant="caption" color="text.secondary">
                      ← このアイコンをクリック
                    </Typography>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '設定メニューが表示される',
              content: (
                <GuideMockContainer label="設定メニュー">
                  <MenuList dense sx={{ pointerEvents: 'none', width: 220 }}>
                    <MenuItem>
                      <ListItemIcon>
                        <BugReportIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>不具合報告</ListItemText>
                    </MenuItem>
                    <MenuItem>
                      <ListItemIcon>
                        <FeedbackIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>フィードバック</ListItemText>
                    </MenuItem>
                  </MenuList>
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      {/* 不具合報告 */}
      <GuideSection title="不具合報告を送る">
        <GuideStepList
          steps={[
            { label: '設定メニューから「不具合報告」をクリックする' },
            {
              label: '不具合報告ダイアログが開くので、発生画面と発生事象を入力する',
              content: (
                <GuideMockContainer label="不具合報告ダイアログ">
                  <Stack spacing={2} sx={{ pointerEvents: 'none' }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      不具合報告
                    </Typography>
                    <TextField
                      label="発生画面"
                      required
                      fullWidth
                      size="small"
                      placeholder="例: パートナー管理画面"
                      helperText="0/50"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      label="発生事象"
                      required
                      fullWidth
                      size="small"
                      multiline
                      rows={4}
                      placeholder="不具合の詳細を記載してください"
                      helperText="0/3000"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          boxShadow: 'none',
                        }}
                      >
                        送信
                      </Button>
                    </Box>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            { label: '「送信」ボタンを押下する' },
          ]}
        />
        <GuideTable
          headers={['項目名', '必須', '形式', '備考']}
          rows={[
            ['発生画面', '必須', 'テキスト（最大50桁）', '不具合が発生した画面名'],
            ['発生事象', '必須', 'テキストエリア（最大3000桁）', '不具合の詳細内容'],
          ]}
        />
      </GuideSection>

      {/* フィードバック */}
      <GuideSection title="フィードバックを送る">
        <GuideStepList
          steps={[
            { label: '設定メニューから「フィードバック」をクリックする' },
            {
              label: 'フィードバックダイアログが開くので、内容を入力する',
              content: (
                <GuideMockContainer label="フィードバックダイアログ">
                  <Stack spacing={2} sx={{ pointerEvents: 'none' }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      フィードバック
                    </Typography>
                    <TextField
                      label="内容"
                      required
                      fullWidth
                      size="small"
                      multiline
                      rows={4}
                      placeholder="改善要望や感想を記載してください"
                      helperText="0/3000"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          boxShadow: 'none',
                        }}
                      >
                        送信
                      </Button>
                    </Box>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            { label: '「送信」ボタンを押下する' },
          ]}
        />
        <GuideTable
          headers={['項目名', '必須', '形式', '備考']}
          rows={[['内容', '必須', 'テキストエリア（最大3000桁）', '改善要望や感想']]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>
            不具合報告・フィードバックは開発者ロールを持つユーザーに通知として送信されます。
          </GuideInfoBox>
          <GuideInfoBox>
            不具合報告は優先度1（最高）、フィードバックは優先度5（最低）で送信されます。
          </GuideInfoBox>
          <GuideInfoBox>送信者名が自動的に内容に付与されます。</GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideFeedback;
